import { createContext, useContext, type ReactNode } from 'react';

/**
 * Wave 2b — caller identity resolved by the worker from the Cloudflare
 * Access JWT (see `/api/me` in worker/main + worker/admin). Provided to
 * the React tree by `permissions-boot.tsx` alongside the permissions
 * array. Hooks that need to know "who am I" (Mine opgaver pre-fill,
 * future personal dashboards) read from here instead of re-fetching.
 *
 * `userId` is the canonical `users.id` UUID; `email` is the JWT email
 * claim. Both are server-attested — never read from `localStorage` or a
 * client-only source.
 */
export interface CurrentUser {
  userId: string;
  email: string;
  /**
   * v3 cutover fix-up — display name resolved from `users.display_name`
   * server-side. Optional so callers built before the field was wired
   * don't break; sidebar + pickers fall back to email when missing.
   */
  displayName?: string | null;
}

const CurrentUserCtx = createContext<CurrentUser | null>(null);

export function CurrentUserProvider({
  user,
  children,
}: {
  user: CurrentUser;
  children: ReactNode;
}) {
  return <CurrentUserCtx.Provider value={user}>{children}</CurrentUserCtx.Provider>;
}

export function useCurrentUser(): CurrentUser {
  const ctx = useContext(CurrentUserCtx);
  if (!ctx) {
    throw new Error(
      'useCurrentUser called outside CurrentUserProvider. Wrap the app in <PermissionsBoot> or supply the provider directly.',
    );
  }
  return ctx;
}

/** Optional variant — returns `null` instead of throwing when unmounted. */
export function useCurrentUserOptional(): CurrentUser | null {
  return useContext(CurrentUserCtx);
}
